import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useDebounce } from "@/hooks/useDebounce";
import { User } from "./types";
import { EmployeeTable } from "./components/EmployeeTable";
import { EmployeeTableSkeleton } from "./components/EmployeeTableSkeleton";
import { AddEmployeeDialog } from "./components/AddEmployeeDialog";
import { EmailEmployeesDialog } from "./components/EmailEmployeesDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Position } from "@/services/positionService";
import { Mail } from "lucide-react";

type SortField = "name" | "email" | "phoneNumber";
type SortDirection = "asc" | "desc";
type UserStatus = "PENDING_APPROVAL" | "ACTIVE" | "INACTIVE" | "TERMINATED";

// Add role options for filter
const roleOptions = [
  { value: "ROLE_USER", label: "User" },
  { value: "ROLE_MODERATOR", label: "Moderator" },
  { value: "ROLE_ADMIN", label: "Admin" },
];

const statusFilters: { value: UserStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING_APPROVAL", label: "Pending Approval" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "TERMINATED", label: "Terminated" },
];

const filterButtonClass =
  "transition-colors px-3 py-1 text-xs font-medium rounded-full border";

export default function ManageEmployees() {
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  // Multi-select for status
  const [selectedStatuses, setSelectedStatuses] = useState<UserStatus[]>([
    "ACTIVE",
  ]);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    undefined,
  );
  const [bilingual, setBilingual] = useState<boolean | undefined>(undefined);
  const [notificationsEnabled, setNotificationsEnabled] = useState<
    boolean | undefined
  >(undefined);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Debounce the search term to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch positions for filter
  const { data: positions } = useQuery({
    queryKey: ["positions"],
    queryFn: async (): Promise<Position[]> => {
      const response = await axiosInstance.get("position");
      return response.data;
    },
  });
  // Build query params
  const params = new URLSearchParams();
  if (selectedStatuses.length > 0) {
    selectedStatuses.forEach((status) => params.append("status", status));
  }
  if (selectedRole) params.append("role", selectedRole);
  if (bilingual !== undefined) params.append("bilingual", String(bilingual));
  if (notificationsEnabled !== undefined)
    params.append("notificationsEnabled", String(notificationsEnabled));
  if (selectedPositions.length > 0) {
    selectedPositions.forEach((positionId) =>
      params.append("positionIds", positionId),
    );
  }
  if (debouncedSearchTerm.trim()) {
    params.append("searchTerm", debouncedSearchTerm.trim());
  }
  const { data: employees, isLoading } = useQuery({
    queryKey: [
      "employees",
      selectedStatuses,
      selectedRole,
      bilingual,
      notificationsEnabled,
      selectedPositions,
      debouncedSearchTerm,
    ],
    queryFn: async (): Promise<User[]> => {
      const response = await axiosInstance.get(`user?${params.toString()}`);
      return response.data;
    },
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="container mx-auto p-6">
      {/* Wraps, and the title is allowed to sit above the buttons rather than
          be squeezed beside them. "Email 12 Employees" and "Add Employee" are
          together about 350px of unbreakable text, which is most of a phone. */}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-foreground">Manage Employees</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Emails exactly the filtered list, so narrow first then send. */}
          <Button
            variant="outline"
            onClick={() => setEmailDialogOpen(true)}
            disabled={isLoading || !employees?.length}
          >
            <Mail className="mr-2 h-4 w-4" />
            Email {employees?.length ?? 0} Employees
          </Button>
          <AddEmployeeDialog />
        </div>
      </div>

      <EmailEmployeesDialog
        isOpen={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        recipients={employees || []}
      />

      <div className="mb-6">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="font-semibold px-6 py-2 rounded-lg border"
            >
              Filters
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[350px] p-5 space-y-6 bg-card border border-border rounded-lg shadow-lg">
            {/* Search Term */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Search by Name or Email
              </span>
              <Input
                placeholder="Type to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Status Multi-Select */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Status
              </span>
              <div className="flex gap-2 flex-wrap">
                {statusFilters.map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={
                      selectedStatuses.includes(filter.value)
                        ? "default"
                        : "outline"
                    }
                    onClick={() => {
                      setSelectedStatuses((prev) =>
                        prev.includes(filter.value)
                          ? prev.filter((s) => s !== filter.value)
                          : [...prev, filter.value],
                      );
                    }}
                    className={cn(
                      filterButtonClass,
                      selectedStatuses.includes(filter.value)
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>
            {/* Role Filter */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Role
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={selectedRole === undefined ? "default" : "outline"}
                  onClick={() => setSelectedRole(undefined)}
                  className={cn(
                    filterButtonClass,
                    selectedRole === undefined
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {roleOptions.map((role) => (
                  <Button
                    key={role.value}
                    size="sm"
                    variant={
                      selectedRole === role.value ? "default" : "outline"
                    }
                    onClick={() => setSelectedRole(role.value)}
                    className={cn(
                      filterButtonClass,
                      selectedRole === role.value
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {role.label}
                  </Button>
                ))}
              </div>
            </div>
            {/* Bilingual Filter */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Bilingual
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={bilingual === undefined ? "default" : "outline"}
                  onClick={() => setBilingual(undefined)}
                  className={cn(
                    filterButtonClass,
                    bilingual === undefined
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={bilingual === true ? "default" : "outline"}
                  onClick={() => setBilingual(true)}
                  className={cn(
                    filterButtonClass,
                    bilingual === true
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant={bilingual === false ? "default" : "outline"}
                  onClick={() => setBilingual(false)}
                  className={cn(
                    filterButtonClass,
                    bilingual === false
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  No
                </Button>
              </div>
            </div>
            {/* Notifications Enabled Filter */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Notifications Enabled
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    notificationsEnabled === undefined ? "default" : "outline"
                  }
                  onClick={() => setNotificationsEnabled(undefined)}
                  className={cn(
                    filterButtonClass,
                    notificationsEnabled === undefined
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={
                    notificationsEnabled === true ? "default" : "outline"
                  }
                  onClick={() => setNotificationsEnabled(true)}
                  className={cn(
                    filterButtonClass,
                    notificationsEnabled === true
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant={
                    notificationsEnabled === false ? "default" : "outline"
                  }
                  onClick={() => setNotificationsEnabled(false)}
                  className={cn(
                    filterButtonClass,
                    notificationsEnabled === false
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  No{" "}
                </Button>
              </div>
            </div>
            {/* Position Filter */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Eligible Positions
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    selectedPositions.length === 0 ? "default" : "outline"
                  }
                  onClick={() => setSelectedPositions([])}
                  className={cn(
                    filterButtonClass,
                    selectedPositions.length === 0
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {positions &&
                  positions
                    .sort((a, b) => a.tableName.localeCompare(b.tableName))
                    .map((position) => (
                      <Button
                        key={position.id}
                        size="sm"
                        variant={
                          selectedPositions.includes(position.id)
                            ? "default"
                            : "outline"
                        }
                        onClick={() => {
                          setSelectedPositions((prev) =>
                            prev.includes(position.id)
                              ? prev.filter((p) => p !== position.id)
                              : [...prev, position.id],
                          );
                        }}
                        className={cn(
                          filterButtonClass,
                          selectedPositions.includes(position.id)
                            ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                            : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        {position.tableName}
                      </Button>
                    ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isLoading ? (
        <EmployeeTableSkeleton />
      ) : (
        <EmployeeTable
          employees={employees || []}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}
    </div>
  );
}
